import {
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import {
  getApplicationSplittingDisabled,
  getApplicationSplittingTooltip
} from "shared/app/ApplicationsTableView/utils/applicationSplittingUtils";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { Action } from "shared/components/ContextMenuPopover";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  ApplicantStatus,
  ApplicationFeeStatus,
  ApplicationStatus,
  RequiresOverridesIdentityVerification
} from "shared/types";
import { Applicant, Application } from "types/application";

import { Archive, ArrowRight, FlipBackward } from "untitledui-js-base";
// import { canDecisionBeMade } from "utils/canMakeDecision";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";

const getRefundTooltip = (applicants?: Applicant[]) => {
  const list = applicants || [];

  if (
    list.some(
      (applicant) => applicant.application_status === ApplicantStatus.Canceled
    )
  )
    return "You can't request a refund for a canceled application";

  if (
    list.some(
      (applicant) => applicant.application_status === ApplicantStatus.Submitted
    )
  )
    if (
      list.every(
        (applicant) =>
          applicant.application_fee_status === ApplicationFeeStatus.Refunded
      )
    )
      return "Refund has already been requested for this application";
    else if (
      list.some(
        (applicant) =>
          applicant.application_fee_status === ApplicationFeeStatus.Paid
      )
    )
      return "";
    else
      return "You can request a refund if someone has paid an application fee";
  else return "You can request a refund for submitted applications";
};

export const usePopoverActions = ({
  applicationId,
  applicants,
  application,
  onMoveClick,
  onRefundClick,
  onCancelClick,
  onUncancelClick,
  onSplitClick,
  applicationStatus,
  requiresOverridesIdentityVerification
}: {
  applicationId: string | number;
  applicationStatus: ApplicationStatus;
  application: Application;
  onMoveClick?: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onRefundClick?: () => void;
  onSplitClick?: () => void;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  applicants?: Applicant[];

  // requirements?: string[];
  // viewTemplate: () => void;
  // setShowActivityLog: (value: boolean) => void;
  // setShowDecisionFlowModal?: (isOpen: boolean) => void;
  // setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  // setDecisionConfirmationModal?: (isOpen: boolean) => void;
}) => {
  const { permissions, type } = useAuthContext();
  const { unit } = useUnitsPropertiesDictionary();

  const queryClient = useQueryClient();

  const {
    // isInviteApplicantDisabled,
    // isMakeDecisionDisabled,
    canResumeApplication,
    canPlaceOnHold,
    isCancelApplicationDisabled,
    isRefundApplicationDisabled,
    isMoveToAnotherUnitDisabled,
    isUncancelApplicationEnabled
  } = useMemo(() => {
    const isMoveToAnotherUnitDisabled =
      (!permissions.canChangeUnit && type.isPropertyManager) ||
      [
        ApplicationStatus.Canceled,
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved
      ].includes(applicationStatus);

    const isCancelApplicationDisabled = [
      ApplicationStatus.Canceled,
      ApplicationStatus.Approved,
      ApplicationStatus.Rejected,
      ApplicationStatus.ConditionalApproved
    ].includes(applicationStatus);

    const isUncancelApplicationEnabled = [ApplicationStatus.Canceled].includes(
      applicationStatus
    );

    const isRefundApplicationDisabled = !(applicants || []).some(
      (applicant: Applicant) =>
        applicant.application_fee_status === ApplicationFeeStatus.Paid &&
        applicant.application_status === ApplicantStatus.Submitted
    );

    const canResumeApplication = applicationStatus === ApplicationStatus.OnHold,
      canPlaceOnHold = applicationStatus === ApplicationStatus.Submitted;

    return {
      // isInviteApplicantDisabled,
      // isMakeDecisionDisabled,
      isCancelApplicationDisabled,
      isRefundApplicationDisabled,
      isMoveToAnotherUnitDisabled,
      isUncancelApplicationEnabled,
      canResumeApplication,
      canPlaceOnHold
    };
  }, [
    applicationStatus,
    type,
    permissions,
    applicants,
    requiresOverridesIdentityVerification
  ]);

  // const canMakeDecision = canDecisionBeMade({
  //   applicants: applicants || [],
  //   status: applicationStatus,
  //   type,
  //   permissions,
  //   requirements
  // });

  const ret: Action[] = [
    {
      title: (
        <MenuRow
          text={`Move to another ${unit}`}
          Icon={ArrowRight}
          isDisabled={isMoveToAnotherUnitDisabled}
        />
      ),
      tooltip: `You can't move application to another ${unit} once the decision have been made`,
      disabled: isMoveToAnotherUnitDisabled,
      onClick: () => {
        onMoveClick && onMoveClick();
      }
    }
  ];

  ret.push({
    title: (
      <MenuRow
        text="Split application"
        HugeIcon={PathfinderDivideIcon}
        isDisabled={getApplicationSplittingDisabled(application)}
      />
    ),
    disabled: getApplicationSplittingDisabled(application),
    tooltip: getApplicationSplittingTooltip(application),
    onClick: () => onSplitClick && onSplitClick()
  });

  if (
    ![
      ApplicationStatus.Approved,
      ApplicationStatus.ConditionalApproved,
      ApplicationStatus.Rejected,
      ApplicationStatus.Canceled
    ].includes(applicationStatus)
  )
    ret.push(
      canResumeApplication
        ? {
            title: <MenuRow text="Resume" HugeIcon={PlayIcon} />,
            onClick: () => {
              resumeApplication({
                applicationId,
                onSuccess: () => {
                  // maybe this is wrong
                  queryClient.refetchQueries({
                    queryKey: ["APPLICATION", applicationId]
                  });
                }
              });
            }
          }
        : {
            title: <MenuRow text="Place on hold" HugeIcon={PauseIcon} />,
            disabled: !canPlaceOnHold,
            tooltip: "Only submitted applications can be put on hold",
            onClick: () => {
              placeOnHoldApplication({
                applicationId,
                onSuccess: () => {
                  // maybe this is wrong
                  queryClient.refetchQueries({
                    queryKey: ["APPLICATION", applicationId]
                  });
                }
              });
            }
          }
    );

  ret.push(
    ...[
      isUncancelApplicationEnabled
        ? {
            title: <MenuRow text="Uncancel application" Icon={Archive} />,
            onClick: () => {
              onUncancelClick && onUncancelClick();
            },
            disabled: applicationStatus === ApplicationStatus.OnHold,
            tooltip: "You need to resume the application before uncanceling"
          }
        : {
            title: (
              <MenuRow
                text="Cancel application"
                Icon={Archive}
                isDisabled={isCancelApplicationDisabled}
              />
            ),
            tooltip:
              applicationStatus === ApplicationStatus.Canceled
                ? "Can not cancel a canceled application"
                : "You can't cancel application once the decisions have been made",
            disabled: isCancelApplicationDisabled,
            onClick: () => {
              onCancelClick && onCancelClick();
            }
          },
      {
        title: (
          <MenuRow
            text="Refund application"
            Icon={FlipBackward}
            isDisabled={isRefundApplicationDisabled}
          />
        ),
        disabled: isRefundApplicationDisabled,
        tooltip: getRefundTooltip(applicants),
        onClick: () => onRefundClick && onRefundClick()
      }
    ]
  );

  return ret;
};
